import clsx from 'clsx';
import { forwardRef, type ElementType, type ReactElement } from 'react';

import * as styles from './NavItem.sty.js';
import { Button } from '../../../buttons/button/Button.js';
import { DataTestId } from '../../../core/types/data-props.js';
import { PolymorphicComponentProps } from '../../../core/types/polymorph.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { WithChildren } from '../../../core/types/with-children.js';


// #region Typings
/**
 * @public
 */
export interface NavItemOwnProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  /**
   * The route where the NavItem navigates to.
   */
  href?: string;
  /**
   * Whether the NavItem is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
  /**
   * Whether the NavItem is selected.
   * @defaultValue false
   */
  isSelected?: boolean;
}

/**
 * @public
 */
export type NavItemProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  NavItemOwnProps
>;
// #endregion

/**
 * A navigation item which can be used to go to a specific route.
 * @public
 */
export const NavItem: <E extends ElementType = 'a'>(
  props: NavItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: NavItemProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      disabled = false,
      isSelected = false,
      as = 'a' as E,
      className: consumerClassName,
      style: consumerStyle = {},
      'data-testid': dataTestId,
      ...remainingProps
    }: NavItemProps<E> = props;

    return (
      <Button
        {...remainingProps}
        ref={forwardedRef}
        disabled={disabled}
        data-testid={dataTestId}
        as={as satisfies ElementType}
        className={clsx(
          styles.button,
          {
            [styles.isSelected]: isSelected,
            [styles.isDisabled]: disabled,
          },
          consumerClassName,
        )}
        style={{
          minWidth: 0,
          flexShrink: 0,
          display: 'flex',
          ...consumerStyle,
        }}
      />
    );
  },
);

(NavItem as typeof NavItem & { displayName: string }).displayName =
  'AppHeader.NavItem';
